import React, { useState, useEffect } from 'react';
import { Plus, BarChart3, Lock, X as XIcon } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import { useAuth } from '../../context/AuthContext';
import { polls as api, type Poll, type PollResults, type ApiError } from '../../services/api';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};

const CREATE_INIT = { question: '', anonymous: false, closesAt: '', options: ['', ''] };

const PollsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const isAdmin = user?.role === 'SOCIETY_ADMIN' || user?.role === 'SUPER_ADMIN';

  const [items, setItems] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<Record<string, PollResults>>({});

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(CREATE_INIT);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [votingId, setVotingId] = useState<string | null>(null);
  const [voteError, setVoteError] = useState('');

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  const loadResults = async (id: string) => {
    if (!societyId) return;
    try {
      const r = await api.getResults(societyId, id);
      setResults(p => ({ ...p, [id]: r }));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    items.forEach(p => {
      if (p.status === 'CLOSED' || p.hasVoted) loadResults(p.id);
    });
  }, [items]);

  const openForm = () => { setForm(CREATE_INIT); setFormError(''); setShowForm(true); };
  const closeForm = () => setShowForm(false);

  const setOption = (idx: number, value: string) =>
    setForm(p => ({ ...p, options: p.options.map((o, i) => i === idx ? value : o) }));
  const addOption = () => setForm(p => ({ ...p, options: [...p.options, ''] }));
  const removeOption = (idx: number) =>
    setForm(p => ({ ...p, options: p.options.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    const cleanOptions = form.options.map(o => o.trim()).filter(Boolean);
    if (cleanOptions.length < 2) { setFormError('Add at least 2 options.'); return; }
    setSaving(true); setFormError('');
    try {
      const created = await api.create(societyId, {
        question: form.question,
        anonymous: form.anonymous,
        closesAt: form.closesAt ? new Date(form.closesAt).toISOString() : undefined,
        options: cleanOptions,
      });
      setItems(p => [created, ...p]);
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to create poll.');
    } finally { setSaving(false); }
  };

  const handleVote = async (poll: Poll, optionId: string) => {
    if (!societyId) return;
    setVotingId(poll.id); setVoteError('');
    try {
      const updated = await api.vote(societyId, poll.id, optionId);
      setItems(p => p.map(x => x.id === updated.id ? updated : x));
      loadResults(poll.id);
    } catch (err) {
      setVoteError((err as ApiError).message || 'Failed to vote.');
    } finally { setVotingId(null); }
  };

  const handleClose = async (poll: Poll) => {
    if (!societyId) return;
    try {
      const updated = await api.close(societyId, poll.id);
      setItems(p => p.map(x => x.id === updated.id ? updated : x));
      loadResults(poll.id);
    } catch { /* ignore */ }
  };

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Polls &amp; Surveys
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>{items.length} polls</p>
        </div>
        {isAdmin && (
          <button onClick={openForm} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
            <Plus size={14} /> New Poll
          </button>
        )}
      </div>

      {voteError && (
        <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, fontSize: 12, color: '#EF4444', marginBottom: 16 }}>
          {voteError}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="nx-card" style={{ padding: 60, textAlign: 'center' }}>
          <BarChart3 size={32} style={{ color: 'var(--nx-text-muted)', display: 'block', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 14, color: 'var(--nx-text-muted)' }}>No polls yet.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map(poll => {
            const r = results[poll.id];
            const hasVoted = poll.hasVoted;
            const showResults = poll.status === 'CLOSED' || hasVoted;
            return (
              <div key={poll.id} className="nx-card" style={{ padding: '18px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)' }}>
                        {poll.question}
                      </h3>
                      {poll.anonymous && <Lock size={12} style={{ color: 'var(--nx-text-muted)' }} />}
                      <span style={{ padding: '1px 6px', borderRadius: 99, fontSize: 10, fontWeight: 600, background: poll.status === 'OPEN' ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.1)', color: poll.status === 'OPEN' ? '#10B981' : '#64748B' }}>
                        {poll.status}
                      </span>
                    </div>
                  </div>
                  {isAdmin && poll.status === 'OPEN' && (
                    <button
                      onClick={() => handleClose(poll)}
                      style={{ width: 28, height: 28, border: 'none', background: 'var(--nx-surface-3)', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nx-text-muted)', flexShrink: 0 }}
                      title="Close poll"
                    >
                      <XIcon size={13} />
                    </button>
                  )}
                </div>

                {showResults && r ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {r.results.map(opt => {
                      const pct = r.totalVotes > 0 ? Math.round((opt.voteCount / r.totalVotes) * 100) : 0;
                      const mine = poll.myVoteOptionId === opt.optionId;
                      return (
                        <div key={opt.optionId}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--nx-text-secondary)', marginBottom: 3 }}>
                            <span>{opt.text}{mine && ' (your vote)'}</span>
                            <span>{opt.voteCount} · {pct}%</span>
                          </div>
                          <div style={{ height: 6, background: 'var(--nx-surface-3)', borderRadius: 99, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: mine ? 'var(--nx-blue)' : 'var(--nx-border)' }} />
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4 }}>{r.totalVotes} total votes</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {poll.options.map(o => (
                      <button
                        key={o.id}
                        onClick={() => handleVote(poll, o.id)}
                        disabled={votingId === poll.id || poll.status !== 'OPEN'}
                        className="nx-btn nx-btn-outline nx-btn-sm"
                        style={{ justifyContent: 'flex-start', opacity: votingId === poll.id ? 0.6 : 1 }}
                      >
                        {o.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <Modal
          title="New Poll" submitLabel="Create Poll" error={formError} isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Question *</label>
            <input value={form.question} onChange={e => setForm(p => ({ ...p, question: e.target.value }))} required placeholder="Should we install solar panels?" style={inp} autoFocus />
          </div>
          <div>
            <label style={lbl}>Options *</label>
            {form.options.map((o, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input value={o} onChange={e => setOption(i, e.target.value)} placeholder={`Option ${i + 1}`} style={inp} />
                {form.options.length > 2 && (
                  <button type="button" onClick={() => removeOption(i)} className="nx-btn nx-btn-outline nx-btn-sm">
                    <XIcon size={12} />
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={addOption} style={{ fontSize: 12, color: 'var(--nx-blue)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
              + Add option
            </button>
          </div>
          <div>
            <label style={lbl}>Closes At</label>
            <input type="datetime-local" value={form.closesAt} onChange={e => setForm(p => ({ ...p, closesAt: e.target.value }))} style={inp} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--nx-text-secondary)' }}>
            <input
              type="checkbox"
              checked={form.anonymous}
              onChange={e => setForm(p => ({ ...p, anonymous: e.target.checked }))}
              style={{ width: 15, height: 15, cursor: 'pointer' }}
            />
            Anonymous voting (voter identities hidden in results)
          </label>
        </Modal>
      )}
    </PageLayout>
  );
};

export default PollsPage;
